import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { Client } from '@langchain/langgraph-sdk';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../AuthContext';
import './SurveyCreateChat.css';

// Message component with markdown support
const Message = ({ message, isAi }) => (
    <div className={`message ${isAi ? 'ai-message' : 'user-message'}`}>
        <div className="message-content">
            {isAi ? (
                <ReactMarkdown>
                    {message.content}
                </ReactMarkdown>
            ) : (
                message.content
            )}
        </div>
    </div>
);

// Survey JSON display component
const SurveyPreview = ({ survey }) => {
    if (!survey) return null;

    return (
        <Card className="survey-preview-card">
            <Card.Header>
                <h5>Survey Preview</h5>
            </Card.Header>
            <Card.Body>
                <h4>{survey.title}</h4>
                <p><strong>Description:</strong> {survey.description || 'No description provided'}</p>
                <p><strong>Context:</strong> {survey.context}</p>
                <p><strong>Tone:</strong> {survey.tone}</p>

                <h5 className="mt-3">Questions:</h5>
                <ol>
                    {survey.questions.map((q, idx) => (
                        <li key={idx} className="mb-2">
                            <div><strong>{q.questionText}</strong></div>
                            <div className="text-muted small">
                                Skippable: {q.isSkippable ? 'Yes' : 'No'} |
                                Clarity: {q.desiredClarity} |
                                Max Follow-ups: {q.maxFollowUps}
                            </div>
                        </li>
                    ))}
                </ol>
            </Card.Body>
        </Card>
    );
};

const SurveyCreateChat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [threadId, setThreadId] = useState(null);
    const [survey, setSurvey] = useState(null);
    const [error, setError] = useState(null);
    const [surveyId, setSurveyId] = useState(null);
    const [authToken, setAuthToken] = useState('');
    const messagesEndRef = useRef(null);
    const navigate = useNavigate();

    // Get Firebase auth context
    const { user } = useAuth();


    // Add this near the other API functions in SurveyCreateChat.jsx
    const fetchLatestSurvey = async () => {
        if (!surveyId) return;

        try {
            // Use the same API base URL as in getGraphConfig
            const baseUrl = process.env.REACT_APP_API_URL+"/api" || 'http://localhost:8081/api';
            const idToken = await user.getIdToken();

            const response = await fetch(`${baseUrl}/surveys/${surveyId}`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch survey: ${response.status}`);
            }

            const surveyData = await response.json();
            setSurvey(surveyData["data"]);
        } catch (err) {
            console.error('Error fetching latest survey:', err);
            setError('Failed to update survey preview. Please try again.');
        }
    };

    // Get current user from context/auth system if available
    const currentUserId = user?.uid || localStorage.getItem('userId') || 'default_user';

    // Set up LangGraph client with proper config
    const langGraphClient = new Client({
        apiUrl: process.env.REACT_APP_LANGGRAPH_API_URL || 'https://smith.katalyze.ai/',
        apiKey: process.env.REACT_APP_LANGGRAPH_API_KEY,
    });

    // Initialize configuration for the LangGraph survey agent
    const getGraphConfig = () => {
        // This matches the API configuration needed in Python
        return {
            user_id: currentUserId,
            thread_id: threadId,
            api_config: {
                base_url:   process.env.REACT_APP_API_URL+"/api" || 'http://localhost:8081/api',
                auth_token: authToken
            }
        };
    };

    // Initialize a new chat thread when component mounts
    useEffect(() => {
        initializeThread();
    }, []);

    // Scroll to the bottom of the chat
    useEffect(() => {
        scrollToBottom();
        fetchLatestSurvey()
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Get the first available assistant
    const getFirstAssistant = async () => {
        try {
            const assistants = await langGraphClient.assistants.search({
                metadata: null,
                limit: 10,
            });
            return assistants.length > 0 ? assistants[0] : null;
        } catch (err) {
            console.error('Error getting assistants:', err);
            return null;
        }
    };

    // Initialize thread with LangGraph
    const initializeThread = async () => {
        try {
            setIsLoading(true);

            // Get Firebase ID token
            if (user) {
                try {
                    const idToken = await user.getIdToken();
                    setAuthToken(idToken);
                } catch (tokenErr) {
                    console.error('Error getting Firebase ID token:', tokenErr);
                    console.warn('No valid authentication token found for survey API');
                }
            } else {
                console.warn('No authenticated user found');
            }

            // Get an assistant to use for survey creation
            const assistant = await getFirstAssistant();
            if (!assistant) {
                throw new Error('No assistants available');
            }

            // Create a new thread
            const thread = await langGraphClient.threads.create();
            setThreadId(thread.thread_id);

            // Add welcome message
            setMessages([{
                id: uuidv4(),
                content: "Welcome to the Survey Creator! I'll help you design a survey step by step. Tell me about the purpose of your survey, and any specific questions you'd like to include.",
                type: 'ai'
            }]);

            setIsLoading(false);
        } catch (err) {
            console.error('Error initializing thread:', err);
            setError('Failed to connect to the survey creation assistant. Please try again later.');
            setIsLoading(false);
        }
    };

    // Update streaming message - NEW FUNCTION
    const updateStreamingMessage = (content) => {
        setMessages(prev => {
            const newMessages = [...prev];
            const streamingIndex = newMessages.findIndex(msg => msg.id === 'streaming-ai');

            if (streamingIndex !== -1) {
                // Update existing streaming message
                newMessages[streamingIndex].content = content;
            } else {
                // Create new streaming message
                newMessages.push({
                    id: 'streaming-ai',
                    content: content,
                    type: 'ai'
                });
            }

            return newMessages;
        });
    };

    // Send a message to the LangGraph API
    const sendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading || !threadId) return;

        // Add user message to chat
        const userMessage = {
            id: uuidv4(),
            content: input,
            type: 'human'
        };
        setMessages(prev => [...prev, userMessage]);

        // Clear input and set loading state
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            // Get the assistant to use
            const assistant = await getFirstAssistant();
            if (!assistant) {
                throw new Error('No assistants available');
            }

            // Create the input for the run with messages and proper configuration
            const messages = [{
                role: "human",
                content: userMessage.content
            }];

            const config = getGraphConfig();

            // Create the input and config objects separately
            const runInput = {
                input: { messages },
                streamMode: ["values"],
                config: config
            };

            // Create the config object with the proper structure

            console.log('Starting stream with:', threadId, assistant.assistant_id, runInput, config);

            // Start a streaming run to get the AI response, passing config separately
            const streamResponse = langGraphClient.runs.stream(
                threadId,
                assistant.assistant_id,
                runInput
            );

            let aiMessageContent = '';

            // Process the streaming response
            for await (const chunk of streamResponse) {
                console.log('Stream chunk received:', chunk);

                // Based on your log, the format is {event: "values", data: {messages: [...]}}
                if (chunk.event === 'values' && chunk.data && chunk.data.messages) {
                    const messages = chunk.data.messages;
                    if (messages && messages.length > 0) {
                        // Get the latest AI message from the array
                        const aiMessages = messages.filter(msg => msg.type === 'ai');
                        const toolMessages = messages.filter(msg => msg.type === 'tool');
                        if (aiMessages.length > 0) {
                            const latestAiMessage = aiMessages[aiMessages.length - 1];
                            aiMessageContent = latestAiMessage.content || '';
                            updateStreamingMessage(aiMessageContent);

                            // Also check for tool calls that might need to be displayed
                            if (latestAiMessage.tool_calls && latestAiMessage.tool_calls.length > 0) {
                                console.log('Tool calls detected:', latestAiMessage.tool_calls);

                                console.log('Tool calls detected:', latestAiMessage.tool_calls);
                            }
                        }
                        if (toolMessages.length > 0 ) {
                                const latestToolMessages = toolMessages[toolMessages.length - 1];
                            if (latestToolMessages.additional_kwargs["survey_id"]){
                                setSurveyId(latestToolMessages.additional_kwargs["survey_id"]);
                                fetchLatestSurvey()
                            }
                        }

                    }
                } else if (chunk.delta?.messages) {
                    // Keep other formats as fallback
                    const messageDelta = chunk.delta.messages[0];
                    if (messageDelta && messageDelta.content) {
                        aiMessageContent += messageDelta.content;
                        updateStreamingMessage(aiMessageContent);
                    }
                }
            }

            // Replace the streaming message ID with a permanent one
            setMessages(prev => {
                const newMessages = [...prev];
                const aiMessageIndex = newMessages.findIndex(msg => msg.id === 'streaming-ai');

                if (aiMessageIndex !== -1) {
                    newMessages[aiMessageIndex].id = uuidv4();
                }

                return newMessages;
            });

            // Check for survey JSON in the AI response
            checkForSurveyJson(aiMessageContent);

        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to get a response. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Check for JSON in the AI response
    const checkForSurveyJson = (content) => {
        try {
            // Look for JSON inside code blocks, which is how the API will typically return it
            const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) ||
                content.match(/FINAL_SURVEY_JSON:\n([\s\S]*?)$/);

            if (jsonMatch && jsonMatch[1]) {
                const surveyData = JSON.parse(jsonMatch[1]);

                // Validate that this looks like a survey object
                if (surveyData.title && surveyData.questions) {
                    setSurvey(surveyData);
                }
            }
        } catch (err) {
            console.error('Error parsing survey JSON:', err);
        }
    };

    // Save the survey to the backend
    const editSurvey = async () => {
        if (!survey) return;
        navigate(`/configure/${survey._id}`)
    };

    return (
        <div className="survey-chat-container">
            <div className="row g-4">
                <div className="col-lg-8">
                    <Card className="chat-card">
                        <Card.Header>
                            <h2>Create Survey with AI</h2>
                            <p className="text-muted">
                                Describe the survey you want to create, and our AI will help you build it.
                            </p>
                        </Card.Header>

                        <Card.Body className="chat-body">
                            {/* Messages container */}
                            <div className="messages-container">
                                {messages.map(message => (
                                    <Message
                                        key={message.id}
                                        message={message}
                                        isAi={message.type === 'ai'}
                                    />
                                ))}

                                {isLoading && (
                                    <div className="ai-message">
                                        <div className="thinking">
                                            <Spinner animation="border" size="sm" />
                                            <span>Thinking...</span>
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>

                            {/* Error message */}
                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    {error}
                                </Alert>
                            )}

                            {/* Input form */}
                            <Form onSubmit={sendMessage} className="chat-input-form">
                                <Form.Group className="d-flex">
                                    <Form.Control
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Type your message..."
                                        disabled={isLoading || !threadId}
                                    />
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={isLoading || !input.trim() || !threadId}
                                    >
                                        Send
                                    </Button>
                                </Form.Group>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-lg-4">
                    {/* Survey preview panel */}
                    {survey ? (
                        <>
                            <SurveyPreview survey={survey} />
                            <div className="d-grid gap-2 mt-3">
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={editSurvey}
                                    disabled={isLoading}
                                >
                                    Edit Survey
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Card className="tips-card">
                            <Card.Header>
                                <h5>Tips for Creating Surveys</h5>
                            </Card.Header>
                            <Card.Body>
                                <ul className="tips-list">
                                    <li>Start by explaining the purpose of your survey</li>
                                    <li>Specify the target audience</li>
                                    <li>Mention how many questions you need</li>
                                    <li>Describe the tone you want (casual, professional, etc.)</li>
                                    <li>You can ask for specific question types</li>
                                    <li>Tell the AI if you want some questions to be skippable</li>
                                </ul>
                            </Card.Body>
                            <Card.Footer className="text-muted">
                                The AI will guide you through the process
                            </Card.Footer>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyCreateChat;